import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import { LabelInput } from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { FloatingLabelSelect } from "../../../../components/Reusable/Layout";
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import {
  fetchCablePlans,
  updateCablePlan,
} from "../../../../features/auth/store/cablePlanSlice";
import Modal from "../../../../components/Reusable/Modal";
import { fetchCableCategories } from "../../../../features/auth/store/cableCategorySlice";
import { fetchCableProviders } from "../../../../features/auth/store/cableProviderSlice";
import { fetchCountries } from "../../../../features/auth/store/countrySlice";
import { fetchCurrencies } from "../../../../features/auth/store/currencySlice";
import { EditCablePlan } from "../../Cable/Plans/types";

type EditCableProps = {
  isOpen: boolean;
  onClose?: () => void;
  cablePlan?: EditCablePlan;
};

export const EditCableModal: React.FC<EditCableProps> = ({
  isOpen,
  onClose,
  cablePlan,
}) => {
  const dispatch = useAppDispatch();

  // Providers and categories from redux
  const { cableProviders } = useAppSelector((state) => state.cableProviders);
  const { cableCategories } = useAppSelector((state) => state.cableCategories);

  const [formData, setFormData] = useState({
    provider_id: "",
    sub_category_id: "",
    tv: "",
    name: "",
    message: "",
    status: "on",
    code: "",
    category: "",
    countries: [] as string[],
    source: "",
    value: "",
    wallet_price: "",
    api_price: "",
    atm_price: "",
    discount: "",
    price_type: "fixed",
    price_currency_id: "",
    index: 1,
  });

  const [image, setImage] = useState<File | undefined>(undefined);
  const [currentImage, setcurrentImage] = useState<string | undefined>(
    undefined
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filteredSubCategories, setFilteredSubCategories] = useState<any[]>([]);
  const countries = useAppSelector((state) => state.countries?.items ?? []);
  const countriesLoading = useAppSelector(
    (state) => state.countries?.loading ?? false
  );
  const currencies = useAppSelector((state) => state.currencies?.items ?? []);
  const deleteOldImage =
    cablePlan?.image_url !== null && currentImage === undefined;

  // Fetch providers and countries on open
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCableProviders());
      dispatch(fetchCountries({ perPage: 200 }));
      dispatch(fetchCurrencies({ perPage: 200 }));
    }
  }, [dispatch, isOpen]);

  // Fetch categories when provider changes
  useEffect(() => {
    if (formData.provider_id) {
      dispatch(fetchCableCategories());
    }
  }, [dispatch, formData.provider_id]);

  // Filter categories for selected provider
  useEffect(() => {
    if (formData.provider_id && cableCategories.length) {
      setFilteredSubCategories(
        cableCategories.filter(
          (cat) => String(cat.provider_id) === String(formData.provider_id)
        )
      );
    } else {
      setFilteredSubCategories([]);
    }
  }, [formData.provider_id, cableCategories]);

  useEffect(() => {
    if (cablePlan) {
      let countries = [] as string[];
      if (cablePlan.countries) {
        const isString = cablePlan.countries.every(
          (country) => typeof country === "string"
        );
        countries = isString
          ? (cablePlan.countries as string[])
          : cablePlan.countries.map((country) => String(country));
      }
      setcurrentImage(cablePlan.image_url);
      setFormData({
        provider_id: cablePlan.category.provider_id?.toString(),
        sub_category_id: cablePlan.category_id?.toString(),
        tv: cablePlan.tv,
        name: cablePlan.name || "",
        message: cablePlan.message,
        status: cablePlan.status,
        code: cablePlan.code || "",
        category: cablePlan.category,
        discount: cablePlan.discount,
        wallet_price: cablePlan.wallet_price?.toString() || "0",
        api_price: cablePlan.api_price?.toString() || "",
        atm_price: cablePlan.atm_price?.toString() || "",
        source: cablePlan.source,
        value: cablePlan.value,
        index: cablePlan.index || 1,
        price_type: cablePlan.price_type || "fixed",
        price_currency_id: cablePlan.price_currency_id?.toString() || "",
        countries,
      });
    }
  }, [cablePlan]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "provider_id" ? { sub_category_id: "" } : {}), // reset subcategory if provider changes
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(e.target.files[0]);
    }
  };

  const removeImage = () => {
    setImage(undefined);
  };

  const toggleCountry = (id: number) => {
    setFormData((prev) => {
      const exists = prev.countries.includes(String(id));
      return {
        ...prev,
        countries: exists
          ? prev.countries.filter((c) => c !== String(id))
          : [...prev.countries, String(id)],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Basic validation
    if (!formData.tv || !formData.name || !formData.source || !formData.value) {
      toast.error("Please fill in all required fields");
      setIsSubmitting(false);
      return;
    }

    if (isNaN(Number(formData.value))) {
      toast.error("Value must be a valid number");
      setIsSubmitting(false);
      return;
    }

    const formDataObj = new FormData();

    // Append all required fields
    formDataObj.append("provider_id", formData.provider_id);
    formDataObj.append("sub_category_id", formData.sub_category_id);
    formDataObj.append("category_id", formData.sub_category_id); // <-- Ensure category_id is sent
    formDataObj.append("tv", formData.tv);
    formDataObj.append("name", formData.name);
    formDataObj.append("message", formData.message);
    formDataObj.append("status", formData.status);
    formDataObj.append("code", formData.code);
    formDataObj.append(
      "countries",
      JSON.stringify(formData.countries.map((c) => Number(c)))
    );
    formDataObj.append("source", formData.source);
    formDataObj.append("value", formData.value);
    formDataObj.append("category", formData.category);
    formDataObj.append("discount", formData.discount);
    formDataObj.append("value", formData.value || "0");
    formDataObj.append("wallet_price", formData.wallet_price || "");
    formDataObj.append("api_price", formData.api_price || "");
    formDataObj.append("atm_price", formData.atm_price || "");
    formDataObj.append("index", String(formData.index || 1));
    formDataObj.append("price_type", formData.price_type || "fixed");
    formDataObj.append("price_currency_id", formData.price_currency_id);
    if (image) {
      formDataObj.append("image", image);
    }
    if (deleteOldImage) {
      formDataObj.append("deleteOldImage", deleteOldImage.toString());
    }

    const filters: any = {};
    // Only add status filter if it's not empty
    if (formData.status && formData.status !== "") {
      filters.status = formData.status;
    }
    try {
      await dispatch(
        updateCablePlan({ data: formDataObj, id: cablePlan?.id as number })
      ).unwrap();
      toast.success("Cable plan edited successfully!");
      handleClose();
      await dispatch(fetchCablePlans(filters)); // Refresh the list after creation
    } catch (error: any) {
      toast.error(error?.message || "Failed to edit cable plan");
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    // Reset form
    setFormData({
      provider_id: "",
      sub_category_id: "",
      tv: "",
      name: "",
      message: "",
      status: "on",
      code: "",
      category: "",
      discount: "",
      countries: [],
      wallet_price: "",
      api_price: "",
      atm_price: "",
      source: "",
      value: "",
      index: 1,
      price_type: "fixed",
      price_currency_id: "",
    });
    setImage(undefined);
    setcurrentImage(undefined);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-white p-6 w-[400px] max-h-[90vh] overflow-y-auto dark:bg-black dark:text-white">
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-base font-semibold">Cable Plan Details</p>
          {/* Provider */}
          <FloatingLabelSelect
            name="provider_id"
            placeholder="Provider"
            value={formData.provider_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Provider" },
              ...cableProviders.map((p) => ({
                value: String(p.id),
                label: p.name,
              })),
            ]}
          />
          {/* Sub Category */}
          <FloatingLabelSelect
            name="sub_category_id"
            placeholder="Sub Category"
            value={formData.sub_category_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Sub Category" },
              ...filteredSubCategories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              })),
            ]}
            disabled={!formData.provider_id}
          />
          {/* Status */}
          <div className="space-y-2">
            <FloatingLabelSelect
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              placeholder="Status"
              options={[
                { value: "on", label: "Active" },
                { value: "off", label: "Inactive" },
              ]}
            />
          </div>

          {/* TV Network */}
          <div className="space-y-2">
            <FloatingLabelSelect
              name="tv"
              placeholder="TV Network"
              value={formData.tv}
              onChange={handleInputChange}
              options={[
                { value: "DSTV", label: "DSTV" },
                { value: "GOTV", label: "GOTV" },
                { value: "STARTIMES", label: "STARTIMES" },
              ]}
            />
          </div>

          {/* Plan Name */}
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="name"
              placeholder="Plan Name"
              value={formData.name}
              onChange={handleInputChange}
            />
          </div>

          {/* Product Category */}
          <div>
            <FloatingLabelSelect
              name="category"
              placeholder="Product Category"
              value={formData.category}
              onChange={handleInputChange}
              options={[
                { value: "prePaid", label: "Prepaid" },
                { value: "postPaid", label: "Postpaid" },
              ]}
            />
          </div>

          {/* Value */}
          <div className="space-y-2">
            <LabelInput
              type="number"
              name="value"
              placeholder="Value"
              value={formData.value}
              onChange={handleInputChange}
            />
          </div>
          {/* Discount Offered  */}
          <LabelInput
            type="number"
            name="discount"
            placeholder="Discount Offered"
            value={formData.discount}
            onChange={handleInputChange}
          />
          {/* Price Type */}
          <FloatingLabelSelect
            name="price_type"
            placeholder="Price Type"
            value={formData.price_type}
            onChange={handleInputChange}
            options={[
              { value: "fixed", label: "Fixed" },
              { value: "percent", label: "Percent" },
            ]}
          />
          {/* Price Currency */}
          <FloatingLabelSelect
            name="price_currency_id"
            placeholder="Price Currency"
            value={formData.price_currency_id}
            onChange={handleInputChange}
            options={[
              { value: "", label: "Select Currency" },
              ...currencies.map((c) => ({
                value: String(c.id),
                label: `${c.name} (${c.symbol})`,
              })),
            ]}
          />

          {/* Source */}
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="source"
              placeholder="Source"
              value={formData.source}
              onChange={handleInputChange}
            />
          </div>

          {/* Code */}
          <div className="space-y-2">
            <LabelInput
              type="text"
              name="code"
              placeholder="Code (Optional)"
              value={formData.code}
              onChange={handleInputChange}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <p className="text-sm font-medium mb-1">Description</p>
            <textarea
              name="message"
              placeholder="Description"
              value={formData.message}
              onChange={handleInputChange}
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500  dark:bg-white/10 dark:text-white"
            />
          </div>

          {/* Countries */}
          <h3 className="text-lg font-semibold text-gray-800 mb-2 dark:text-white">
            Countries (availability)
          </h3>
          <div className="border rounded-md p-3 max-h-48 overflow-y-auto">
            {countriesLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
              </div>
            ) : countries.length === 0 ? (
              <p className="text-sm text-gray-500">
                No countries found. Create countries first.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {countries.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={formData.countries.includes(String(c.id))}
                      onChange={() => toggleCountry(c.id)}
                    />
                    <span>
                      {c.name} ({c.symbol})
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Product Images */}
          <div className="space-y-4">
            <p className="text-base font-semibold">Product Images</p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center  dark:bg-white/10 dark:text-white">
              <input
                type="file"
                id="fileUpload"
                onChange={handleFileChange}
                className="hidden"
                accept="image/*"
                multiple
              />
              <label
                htmlFor="fileUpload"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mb-2"
                >
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17 8L12 3L7 8"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 3V15"
                    stroke="#718096"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm text-gray-600">
                  Upload from computer
                </span>
              </label>
            </div>
            {image && (
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="relative rounded-md overflow-hidden h-20">
                  <img
                    src={URL.createObjectURL(image)}
                    alt="product"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage()}
                    className="absolute top-1 right-1 bg-white rounded-full p-1"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 6L18 18"
                        stroke="#E53E3E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            {currentImage && (
              <div>
                <p className="text-base font-semibold">Current Product Image</p>
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="relative rounded-md overflow-hidden h-20">
                    <img
                      src={currentImage}
                      alt="product"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setcurrentImage(undefined)}
                      className="absolute top-1 right-1 bg-white rounded-full p-1"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 6L18 18"
                          stroke="#E53E3E"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6">
            <Button
              type="submit"
              disabled={isSubmitting}
              bgColor={
                isSubmitting
                  ? "bg-gray-300"
                  : "bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              }
              textColor="text-white"
              borderRadius="rounded-lg"
              className="w-full h-10"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
